/**
 * SAMPLE DATA ONLY — battery (akü), lift (direk) and eco-driving reports of the demo mode (ADR 0034). The
 * demo Teltonika vehicles get the simulator's deterministic sample day (packages/core sampleDay) per local
 * day; the reports run the same core rules the database mirrors (batteryVoltageOf, chargeSessionsFromVoltage,
 * liftCycles, lowBatteryStep). Vehicles without a battery source show "no battery source", never a voltage.
 */
import {
  ANALOG_DEFAULTS,
  CHARGE_END_V_PER_CELL,
  CHARGE_RULES,
  CHARGE_V_PER_CELL,
  chargeLevelV,
  initialLowBatteryState,
  DRIVING_HIDDEN_MIN_CARDS,
  TELEMATICS_REPORT_LIMITS,
  batteryTotals,
  batteryVoltageOf,
  chargeSessionsFromVoltage,
  deviceFamily,
  liftCycles,
  liftValueOf,
  lowBatteryStep,
  sampleDay,
  type BatteryResult,
  type BatteryVehicle,
  type Device,
  type DrivingGroup,
  type DrivingRowKind,
  type EcoEventRow,
  type EcoEventType,
  type EcoResult,
  type EcoRow,
  type LiftResult,
  type LiftRow,
  type LowBatteryState,
  type SampleDayPoint,
  type Vehicle,
  type VehicleAnalogCalibration,
} from "@fts/core";

export type DemoTelematicsCtx = {
  now: number;
  days: string[];
  dayStart(day: string): number;
  vehicles: Vehicle[];
  device(v: Vehicle): Device | undefined;
  calibration(v: Vehicle): VehicleAnalogCalibration;
  drivers(v: Vehicle): string[];
  operatorVisible(id: string): boolean;
  operatorName(id: string): string | null;
};

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

const cache = new Map<string, SampleDayPoint[]>();

/** The sample day of a demo Teltonika vehicle (empty for other families); nothing after now. */
export function demoSamples(ctx: DemoTelematicsCtx, v: Vehicle, day: string): (SampleDayPoint & { op: string | null })[] {
  const d = ctx.device(v);
  if (!d || deviceFamily(d) !== "teltonika") return [];
  const key = `${v.id}|${day}`;
  let pts = cache.get(key);
  if (!pts) {
    const scale = (v.batteryVoltage ?? 48) / 48;
    pts = sampleDay(ctx.dayStart(day), { seed: hash(key), stepS: 30, wear: (hash(v.id) % 10) / 10 }).map((p) =>
      scale === 1 ? p : { ...p, batV: Math.round(p.batV * scale * 100) / 100, ain2Mv: Math.round(((p.batV * scale) / 4) * 1000) },
    );
    if (cache.size > 400) cache.clear();
    cache.set(key, pts);
  }
  const drivers = ctx.drivers(v);
  const noon = ctx.dayStart(day) + 12.5 * 3600_000;
  return pts
    .filter((p) => p.at < ctx.now)
    .map((p) => ({ ...p, op: p.ign ? (p.at < noon ? drivers[0] ?? null : drivers[1] ?? drivers[0] ?? null) : null }));
}

const sampleValues = (p: SampleDayPoint) => ({ batV: null, vin: p.extMv / 1000, ain1Mv: p.ain1Mv, ain2Mv: p.ain2Mv });

export function demoBattery(ctx: DemoTelematicsCtx, tz: string): BatteryResult {
  const bucketS = TELEMATICS_REPORT_LIMITS.seriesBucketS;
  const vehicles: BatteryVehicle[] = [...ctx.vehicles]
    .sort((a, b) => a.fleetNo.localeCompare(b.fleetNo, "tr", { numeric: true }))
    .map((v) => {
      const cal = ctx.calibration(v);
      const d = ctx.device(v);
      const pts = ctx.days.flatMap((day) => demoSamples(ctx, v, day));
      const read = cal.batterySource === "none" ? [] : pts.map((p) => ({ ts: p.at, ign: p.ign, speed: p.speedKmh, lift: liftValueOf(sampleValues(p), cal), v: batteryVoltageOf(sampleValues(p), cal) }));
      const vals = read.filter((r): r is typeof r & { v: number } => r.v !== null);
      const levelV = chargeLevelV(cal.chargeV, v.batteryVoltage);
      const buckets = new Map<number, number[]>();
      for (const r of vals) {
        const b = Math.floor(r.ts / 1000 / bucketS) * bucketS * 1000;
        (buckets.get(b) ?? buckets.set(b, []).get(b)!).push(r.v);
      }
      let st: LowBatteryState = initialLowBatteryState();
      let lowAlarms = 0;
      if (cal.batLowV !== null) {
        for (const r of vals) {
          const lifting = r.lift !== null && cal.liftOff !== null && r.lift > cal.liftOff;
          const s = lowBatteryStep(st, { ts: r.ts, v: r.v, ign: r.ign, speedKmh: r.speed, lifting }, cal.batLowV);
          st = s.state;
          if (s.alarm) lowAlarms++;
        }
      }
      const r2 = (x: number) => Math.round(x * 100) / 100;
      return {
        vehicleId: v.id,
        fleetNo: v.fleetNo,
        family: d ? deviceFamily(d) : null,
        source: cal.batterySource,
        readings: vals.length,
        minV: vals.length ? Math.min(...vals.map((x) => x.v)) : null,
        maxV: vals.length ? Math.max(...vals.map((x) => x.v)) : null,
        avgV: vals.length ? r2(vals.reduce((a, x) => a + x.v, 0) / vals.length) : null,
        lowV: cal.batLowV,
        lowReadings: cal.batLowV === null ? 0 : vals.filter((x) => x.v < cal.batLowV!).length,
        lowAlarms,
        riseV: cal.chargeRiseV,
        levelV,
        series: [...buckets.entries()].sort((a, b) => a[0] - b[0]).map(([b, xs]) => ({
          at: new Date(b).toISOString(),
          avgV: r2(xs.reduce((a, x) => a + x, 0) / xs.length),
          minV: Math.min(...xs),
          maxV: Math.max(...xs),
        })),
        sessions: chargeSessionsFromVoltage(read, { levelV, riseV: cal.chargeRiseV }).map((s) => ({
          start: new Date(s.start).toISOString(),
          end: new Date(s.end).toISOString(),
          durS: Math.round(s.durS),
          startV: s.startV,
          endV: s.endV,
          peakV: s.peakV,
          opportunity: s.opportunity,
          open: s.open,
        })),
      };
    });
  return {
    tz,
    bucketS,
    rules: {
      minS: ANALOG_DEFAULTS.chargeMinS, opportunityMaxS: ANALOG_DEFAULTS.opportunityMaxS, holdS: CHARGE_RULES.holdS, settleS: CHARGE_RULES.settleS,
      medianN: CHARGE_RULES.medianN, levelVPerCell: CHARGE_V_PER_CELL, endVPerCell: CHARGE_END_V_PER_CELL,
    },
    vehicles,
    totals: batteryTotals(vehicles),
  };
}

type Keyed = { kind: DrivingRowKind; id: string | null; name: string | null; card: string | null };

function keyOf(ctx: DemoTelematicsCtx, group: DrivingGroup, v: Vehicle, op: string | null): Keyed {
  if (group === "vehicle") return { kind: "vehicle", id: v.id, name: v.fleetNo, card: op };
  if (op === null) return { kind: "no_card", id: null, name: null, card: null };
  if (!ctx.operatorVisible(op)) return { kind: "hidden", id: null, name: null, card: op };
  return { kind: "operator", id: op, name: ctx.operatorName(op), card: op };
}

const KIND_ORDER: Record<DrivingRowKind, number> = { operator: 1, vehicle: 1, hidden: 2, unknown_card: 3, no_card: 4 };

/** Groups items into rows (KVKK: a single hidden card is suppressed and left out of the totals). */
function grouped<T, M>(ctx: DemoTelematicsCtx, group: DrivingGroup, items: { v: Vehicle; op: string | null; m: T }[], empty: () => M, add: (m: M, x: T) => void) {
  const rows = new Map<string, Keyed & { m: M; cards: Set<string>; veh: Set<string> }>();
  for (const it of items) {
    const k = keyOf(ctx, group, it.v, it.op);
    const key = `${k.kind}:${k.id ?? ""}`;
    let r = rows.get(key);
    if (!r) rows.set(key, (r = { ...k, m: empty(), cards: new Set(), veh: new Set() }));
    add(r.m, it.m);
    if (k.card && k.kind === "hidden") r.cards.add(k.card);
    r.veh.add(it.v.id);
  }
  const hidden = rows.get("hidden:");
  const suppressHidden = !!hidden && hidden.cards.size < DRIVING_HIDDEN_MIN_CARDS;
  const list = [...rows.values()].sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || (a.name ?? "").localeCompare(b.name ?? "", "tr", { numeric: true }));
  return { list, suppressHidden, totalItems: items.filter((it) => !(suppressHidden && keyOf(ctx, group, it.v, it.op).kind === "hidden")) };
}

export function demoLift(ctx: DemoTelematicsCtx, tz: string, group: DrivingGroup, operatorId: string | null): LiftResult {
  const items: { v: Vehicle; op: string | null; m: { durS: number; driveS: number; wd: boolean } }[] = [];
  const unconfigured: LiftResult["unconfigured"] = [];
  for (const v of [...ctx.vehicles].sort((a, b) => a.fleetNo.localeCompare(b.fleetNo, "tr", { numeric: true }))) {
    const cal = ctx.calibration(v);
    if (cal.liftSource === "none" || cal.liftOn === null || cal.liftOff === null) {
      unconfigured.push({ vehicleId: v.id, fleetNo: v.fleetNo });
      continue;
    }
    const pts = ctx.days.flatMap((day) => demoSamples(ctx, v, day));
    for (const c of liftCycles(pts.map((p) => ({ ts: p.at, value: liftValueOf(sampleValues(p), cal), speedKmh: p.speedKmh, ign: p.ign, op: p.op })), { on: cal.liftOn, off: cal.liftOff, driveKmh: cal.liftDriveKmh })) {
      if (operatorId && c.op !== operatorId) continue;
      items.push({ v, op: c.op, m: { durS: c.durS, driveS: c.driveS, wd: c.whileDriving } });
    }
  }
  const zero = () => ({ lifts: 0, liftS: 0, whileDriving: 0, whileDrivingS: 0 });
  const add = (m: ReturnType<typeof zero>, x: { durS: number; driveS: number; wd: boolean }) => {
    m.lifts++;
    m.liftS += x.durS;
    m.whileDrivingS += x.driveS;
    if (x.wd) m.whileDriving++;
  };
  const g = grouped(ctx, group, items, zero, add);
  const totals = zero();
  for (const it of g.totalItems) add(totals, it.m);
  const rows: LiftRow[] = g.list.map((r) =>
    r.kind === "hidden" && g.suppressHidden
      ? { kind: r.kind, id: null, name: null, cards: r.cards.size, vehicles: 0, suppressed: true, ...zero() }
      : { kind: r.kind, id: r.id, name: r.name, cards: r.cards.size, vehicles: r.veh.size, suppressed: false, ...r.m, liftS: Math.round(r.m.liftS) },
  );
  return {
    tz,
    group,
    rows,
    totals: { ...totals, liftS: Math.round(totals.liftS), vehicles: new Set(g.totalItems.map((i) => i.v.id)).size, operators: new Set(g.totalItems.map((i) => i.op).filter((o) => o && ctx.operatorVisible(o))).size },
    unconfigured,
  };
}

const ECO_TYPES: Record<1 | 2 | 3, EcoEventType> = { 1: "harsh_accel", 2: "harsh_brake", 3: "harsh_corner" };

export function demoEco(ctx: DemoTelematicsCtx, tz: string, group: DrivingGroup, operatorId: string | null): EcoResult {
  const items: { v: Vehicle; op: string | null; m: EcoEventRow }[] = [];
  const vs = [...ctx.vehicles].sort((a, b) => a.fleetNo.localeCompare(b.fleetNo, "tr", { numeric: true }));
  for (const v of vs) {
    for (const day of ctx.days) {
      for (const p of demoSamples(ctx, v, day)) {
        if (!p.green || (operatorId && p.op !== operatorId)) continue;
        items.push({
          v,
          op: p.op,
          m: { at: new Date(p.at).toISOString(), vehicleId: v.id, fleetNo: v.fleetNo, type: ECO_TYPES[p.green.type], g: p.green.value / 100, operatorName: p.op && ctx.operatorVisible(p.op) ? ctx.operatorName(p.op) : null, lat: p.lat, lon: p.lon },
        });
      }
    }
  }
  const zero = () => ({ accel: 0, brake: 0, corner: 0, total: 0, maxG: null as number | null });
  const add = (m: ReturnType<typeof zero>, e: EcoEventRow) => {
    if (e.type === "harsh_accel") m.accel++;
    else if (e.type === "harsh_brake") m.brake++;
    else m.corner++;
    m.total++;
    if (e.g !== null) m.maxG = Math.max(m.maxG ?? 0, e.g);
  };
  const g = grouped(ctx, group, items, zero, add);
  const totals = zero();
  for (const it of g.totalItems) add(totals, it.m);
  const rows: EcoRow[] = g.list.map((r) =>
    r.kind === "hidden" && g.suppressHidden
      ? { kind: r.kind, id: null, name: null, cards: r.cards.size, vehicles: 0, suppressed: true, ...zero() }
      : { kind: r.kind, id: r.id, name: r.name, cards: r.cards.size, vehicles: r.veh.size, suppressed: false, ...r.m },
  );
  // KVKK: events of operators the caller may not see are counted, never listed (any grouping).
  const hiddenOf = (it: { op: string | null }) => it.op !== null && !ctx.operatorVisible(it.op);
  const events = items.filter((i) => !hiddenOf(i)).map((i) => i.m).sort((a, b) => a.at.localeCompare(b.at));
  return {
    tz,
    group,
    rows,
    totals: { ...totals, vehicles: new Set(g.totalItems.map((i) => i.v.id)).size, operators: new Set(g.totalItems.map((i) => i.op).filter((o) => o && ctx.operatorVisible(o))).size },
    events: events.slice(0, TELEMATICS_REPORT_LIMITS.maxEcoEvents),
    truncated: events.length > TELEMATICS_REPORT_LIMITS.maxEcoEvents,
    hiddenEvents: items.filter(hiddenOf).length,
    sources: vs.map((v) => {
      const d = ctx.device(v);
      const fam = d ? deviceFamily(d) : null;
      return { vehicleId: v.id, fleetNo: v.fleetNo, family: fam, eco: fam === "teltonika" };
    }),
  };
}
